import type { JSX } from 'react';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { COMMUNITY_FEATURES, FeatureGrid } from '@/features/organization/membership-features';

/**
 * "Ingin ada ustadz yang membimbing?" - how to join a pondok, and what
 * joining adds.
 *
 * This used to live inside `NoMembershipPanel`, which renders only while
 * the account belongs to nothing at all. Starting a personal space
 * therefore made the entire explanation vanish: a solo learner had no way
 * left to find out that joining was possible, let alone how. That
 * contradicted ADR-031's own premise - a pondok is an additive layer, and
 * "Ruang pribadi Anda tetap ada setelah bergabung."
 *
 * `showsWhatIsWaiting` states the practical consequence of memorizing
 * alone. A solo learner holds `memorization_records:manage` but only
 * `evaluations:read` and `murajaah_sessions:read`, so their setoran stays
 * "Menunggu Evaluasi" and their coverage stays 0% until an ustadz can
 * verify it. That is ADR-031's deliberate design - verification belongs to
 * a teacher - but leaving it unexplained makes a working app look broken.
 */
export function JoinPondokCard({
  showsWhatIsWaiting = false,
}: {
  /** True inside a personal space, where nobody can evaluate or schedule murajaah yet. */
  readonly showsWhatIsWaiting?: boolean;
}): JSX.Element {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Ingin ada ustadz yang membimbing?</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4 text-sm">
        <p className="leading-relaxed text-muted-foreground">
          Bergabung ke pondok atau rumah tahfidz menambahkan komunitas di atas hafalan Anda: ustadz
          yang menguji dan memotivasi, serta ruang khusus untuk materi dan bimbingan. Bergabung
          selalu lewat{' '}
          <strong className="text-foreground">undangan dari pengelola pondok</strong> - bukan lewat
          pendaftaran mandiri - supaya tidak ada orang luar yang bisa masuk ke data santri sebuah
          pondok.
        </p>

        {showsWhatIsWaiting && (
          <div className="flex flex-col gap-2 rounded-md border border-border bg-secondary/40 p-3">
            <span className="text-sm font-medium">Yang menunggu seorang ustadz</span>
            <ul className="flex list-disc flex-col gap-1 pl-4 text-sm text-muted-foreground">
              <li>
                Setoran Anda tercatat, tetapi statusnya tetap{' '}
                <strong className="text-foreground">Menunggu Evaluasi</strong> sampai ada ustadz
                yang menyimak dan menilainya.
              </li>
              <li>
                Karena penilaian itulah yang menandai ayat sebagai selesai,{' '}
                <strong className="text-foreground">Ayat selesai</strong> dan cakupan target masih
                0% - bukan karena hafalan Anda tidak dihitung, tetapi karena belum ada yang menguji.
              </li>
              <li>Jadwal murajaah juga disusun oleh ustadz pembimbing, bukan dibuat sendiri.</li>
            </ul>
            <p className="text-xs leading-relaxed text-muted-foreground">
              Ini memang disengaja: dalam tradisi talaqqi, hafalan disimak dan disahkan oleh guru,
              bukan dinilai sendiri. Target, setoran, dan ritme Anda tetap tersimpan utuh sampai
              saat itu tiba.
            </p>
          </div>
        )}

        <div className="flex flex-col gap-2 rounded-md border border-border bg-card p-3">
          <span className="text-sm font-medium">Cara bergabung</span>
          <ol className="flex list-decimal flex-col gap-1 pl-4 text-sm text-muted-foreground">
            <li>Minta tautan undangan kepada pengelola pondok Anda.</li>
            <li>Buka tautan itu sambil tetap masuk dengan akun ini.</li>
            <li>Terima undangan - peran Anda langsung aktif di pondok tersebut.</li>
          </ol>
        </div>
        <p className="text-xs text-muted-foreground">
          Ruang pribadi Anda tetap ada setelah bergabung. Keduanya bisa dipakai bersamaan.
        </p>
        <FeatureGrid features={COMMUNITY_FEATURES} />
      </CardContent>
    </Card>
  );
}
